import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { InputTextarea } from 'primereact/inputtextarea'
import { Checkbox } from 'primereact/checkbox'
import { hapticEffects } from '../../lib/hapticEffects'
import Grid from '../_common/Grid/Grid'
import SkipBlock from '@aytm/app/swidget/base/modules/preview/components/SkipBlock'
import PrequalBlock from '@aytm/app/swidget/base/modules/preview/components/PrequalBlock'
import TextWithOptions from '@aytm/app/swidget/base/modules/preview/components/TextWithOptions'

import { IQuestionViewProps } from '../types'
import { CheckboxesQuestionModelTypes, ICheckboxesQuestionSchemaAnswer } from './types'
import { ViewportOrientation } from '../../modules/viewport/types'

import useViewport from '../../modules/viewport/useViewport'
import useImages from '../../modules/images/useImages'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useTimelineFrame from '../../modules/timeline/useTimelineFrame'
import useOpenEndedLogger from '../../modules/supervisor/useOpenEndedLogger'
import useFirstMountEffect from '@aytm/lib/hooks/useFirstMountEffect'
import useSession from '@aytm/app/swidget/base/session/useSession'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'

import commonStyle from '../../questions/_common/style.module.scss'
import style from './style.module.scss'
import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'
import { getPlainTextFromFormatted } from '../../lib/accessibility'

const onCommentFocus = (event: SyntheticEvent) => {
  const el = event.target as HTMLInputElement
  if (!el) return
  el.setSelectionRange(el.value.length, el.value.length)
  hapticEffects.soft()
}

const CheckboxesView = ({
  schema,
  response,
  timeline,
  responseValidation,
  showValidation,
  frame,
  onUpdateResponse
}: IQuestionViewProps<CheckboxesQuestionModelTypes>) => {
  const [na, setNA] = useState(false)

  const { ImageAsset } = useImages()
  const viewport = useViewport()
  const session = useSession()
  const previewOptions = usePreviewOptions()
  const { localeOptions } = useInterfaceLocale()
  const disabled = false == session.rules?.show?.[schema.id] && !session.skipValidation
  const [iClicked, setIClicked] = useState(false)
  const { t } = useSurveyLocale()
  const sub = schema.parentId ? session.rules?.sub?.[schema.parentId] : session.rules?.sub?.[schema.id]
  const maximumSelectedReached = schema.max_choices
    ? response.filter(answer => answer.selected)?.length === schema.max_choices
    : false
  const questionNote = t<(n1: number, n2: number) => string>(
    'questions',
    'cb',
    'note'
  )(schema.min_choices || 0, schema.max_choices || 5000)

  useEffect(() => {
    setNA(false)
  }, [frame])

  const updateExclusives = () => {
    const exArray: Array<number> = []
    response.forEach((ra, raIndex) => {
      if (!ra.selected) return
      const answer = schema.answers[raIndex]
      if (answer.exclusive?.length)
        answer.exclusive?.forEach(exId => {
          const exIndex = schema.answers.findIndex(a => a.id == exId)
          response[exIndex].selected = false
          exArray.push(exIndex)
        })
    })
    response.forEach((ra, raIndex) => (ra.disabled = exArray.includes(raIndex)))
  }

  const onSelectedChange = (answerIndex: number, answerId?: string) => {
    if (iClicked) {
      setIClicked(false)
      return
    }
    timeline._log({ type: 'answer', target: answerId })
    response[answerIndex].selected = !response[answerIndex].selected
    if (schema.answers[answerIndex].na) {
      schema.answers.forEach((_answer, aI) => {
        if (aI != answerIndex) response[aI].disabled = response[answerIndex].selected
      })
      setNA(response[answerIndex].selected)
    } else updateExclusives()

    if (response[answerIndex].selected) {
      hapticEffects.selection()
    }
    onUpdateResponse(response)
  }

  const onImageClicked = (event: SyntheticEvent, answerIndex: number, answer?: ICheckboxesQuestionSchemaAnswer) => {
    if (
      viewport.mobile || viewport.touch ? !answer?.image?.expand : !(event.target as HTMLElement).closest('.answerRow')
    ) {
      setIClicked(true)
      setTimeout(() => {
        setIClicked(false)
      }, 100)
      return
    }
    const el = event.target as Element
    if (
      event?.target &&
      ('I' == el.tagName ||
        el.classList.contains('p-component-overlay') ||
        el.classList.contains('p-image-preview') ||
        el.parentElement?.classList.contains('p-image-preview-enter-done'))
    )
      return
    onSelectedChange(answerIndex, answer.id)
  }

  const openEndedLogger = useOpenEndedLogger(timeline, schema.id)
  useFirstMountEffect(() => void response.forEach(answer => void openEndedLogger(answer.comment, answer.id, false)))

  const onCommentChange = (answerIndex: number, event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // response[answerIndex].comment = event.target.value
    // onUpdateResponse(response)
    onUpdateResponse(
      response.map((responseAnswer, responseAnswerIndex) => {
        if (responseAnswerIndex === answerIndex) {
          responseAnswer.comment = event.target.value
          openEndedLogger(responseAnswer.comment, responseAnswer.id)
        }
        return responseAnswer
      })
    )
  }

  const naAnswerId = useRef(schema.answers.find(answer => answer.na)?.id)
  const naAnswerIndex = useRef(naAnswerId.current ? response.findIndex(r => r.id == naAnswerId.current) : null)

  const hasSmallImages = schema.answers.some(answer => answer.image && !answer.image.expand)
  const frameInteractionStart = useTimelineFrame(timeline, frame)
  const showIcon =
    viewport.mobile && viewport.orientation == ViewportOrientation.PORTRAIT && schema.image_grid_columns > 2

  const isAnswerSelected = (answerId: string) =>
    !response.some(a => a.selected && a.id === answerId) && answerId !== naAnswerId.current

  return (
    <fieldset
      className={`
        ${style.CheckboxesView}
        ${localeOptions.rtl ? style.CheckboxesViewRTL : ''}
        ${viewport.mobile ? commonStyle.mobileView + ' ' + style.mobile : ''}
        ${disabled ? commonStyle.disabled : ''}
      `}
      aria-describedby={`questionNote-${schema.id}`}
      data-testid="SW-CB"
    >
      {!schema.answers.length
        ? getPlainTextFromFormatted(t('questions', 'noAnswers'))
        : !showIcon && (
            <>
              <div
                id={`questionNote-${schema.id}`}
                className={`
                  ${commonStyle.validationMessage}
                  ${showValidation && !responseValidation.valid ? commonStyle.error : ''}
                `}
                data-testid="SW-CB-ValidationMessage"
                dangerouslySetInnerHTML={{
                  __html: questionNote
                }}
              />
              <legend className={commonStyle.srOnly}>{schema.text}</legend>
            </>
          )}
      {schema.image_grid_columns ? (
        <>
          <Grid
            schema={schema}
            response={response}
            onSelectedChange={onSelectedChange}
            onCommentChange={onCommentChange}
            na={na}
            role="checkbox"
          />
          {!showIcon && naAnswerIndex.current !== null && response[naAnswerIndex.current] && (
            <label className={style.AnswerRow} data-testid="SW-CB-Answer">
              <Checkbox
                value={naAnswerId.current}
                name="answer"
                checked={response[naAnswerIndex.current].selected}
                data-testid="SW-RB-Answer-Checkbox"
                onChange={() => onSelectedChange(naAnswerIndex.current, naAnswerId.current)}
              />
              <div className={style.AnswerTextBlock}>
                <span
                  className={style.AnswerText}
                  data-testid="SW-CB-Answer-Text"
                  dangerouslySetInnerHTML={{ __html: schema.answers[naAnswerIndex.current].text }}
                />
              </div>
            </label>
          )}
        </>
      ) : (
        schema.answers.map((answer, answerIndex) => {
          const responseAnswer = response[answerIndex]
          return (
            <div
              className={`
                ${style.AnswerFrame}
                ${answer.hidden ? commonStyle.hidden : ''}
                ${responseAnswer.disabled ? commonStyle.disabled : ''}
                ${answer.fakeHidden ? commonStyle.fakeHidden : ''}
              `}
              key={answer.id}
              data-testid="SW-CB-Answer"
            >
              <div className={`${style.AnswerRow} answerRow`}>
                <div className={style.noWrap}>
                  {answer.image && !answer.image?.expand && (
                    <div onClick={event => onImageClicked(event, answerIndex, answer)} data-testid="SW-CB-Answer-Image">
                      <ImageAsset
                        position={localeOptions.rtl ? 'right' : 'left'}
                        asset={answer.image}
                        className={style.AnswerImage}
                        width={66}
                        height={36}
                        size="small"
                        previewLabelTemplate={() => (
                          <TextWithOptions text={answer.text} options={previewOptions} rules={sub?.[answer.id]} />
                        )}
                      />
                    </div>
                  )}

                  {(!answer.image || answer.image.expand) && hasSmallImages && (
                    <ImageAsset width={66} height={36} size="small" asset={null} className={style.AnswerImage} />
                    // <div className={style.AnswerVoidImage} />
                  )}
                  <div className={commonStyle.srOnly} aria-hidden={!maximumSelectedReached} id="maximumSelectedNote">
                    {t('questions', 'cb', 'max')}
                  </div>
                  <label
                    htmlFor={answer.id}
                    className={style.AnswerLabel}
                    onChange={() => onSelectedChange(answerIndex, answer.id)}
                    aria-label={answer.text}
                    {...(maximumSelectedReached &&
                      isAnswerSelected(answer.id) && { 'aria-describedby': 'maximumSelectedNote' })}
                  >
                    <Checkbox
                      inputId={answer.id}
                      value={answer.id}
                      name="answer"
                      data-testid="SW-RB-Answer-Checkbox"
                      checked={responseAnswer.selected}
                      disabled={!schema.answers[answerIndex].na && na}
                    />
                    <div className={commonStyle.srOnly} aria-live="polite">
                      {responseAnswer?.selected && answer.oe !== 'off' ? `${t('questions', 'oe', 'hint')}` : ''}
                    </div>
                    <div className={style.AnswerTextBlock}>
                      <span className={style.AnswerText} data-testid="SW-CB-Answer-Text">
                        {(previewOptions.showHidden || !session.rules?.hide_text?.[answer.id]?.condition) && (
                          <TextWithOptions text={answer.text} options={previewOptions} rules={sub?.[answer.id]} />
                        )}
                      </span>
                    </div>
                    <div className={style.rightBlock}>
                      {previewOptions.showHints && <SkipBlock rules={session.rules?.skip?.[answer.id]} />}
                      {previewOptions.showHints && schema.is_prequal && <PrequalBlock accept={answer.accept} />}
                    </div>
                  </label>
                </div>
                {answer.oe && 'off' !== answer.oe && responseAnswer.selected && (
                  <label
                    htmlFor={`textarea-${answer.id}`}
                    className={style.AnswerTextAreaLabel}
                    aria-label={answer.text}
                  >
                    <InputTextarea
                      id={`textarea-${answer.id}`}
                      className={`${style.AnswerTextArea} ${
                        showValidation && !responseAnswer?.comment?.length && 'mandatory' == answer.oe && !na
                          ? style.AnswerTextAreaError
                          : ''
                      } ${hasSmallImages ? style.AnswerTextAreaWithImage : ''}`}
                      placeholder={t('questions', 'placeholder')}
                      rows={3}
                      autoResize={true}
                      autoFocus={false}
                      value={responseAnswer.comment}
                      data-testid="SW-CB-Answer-OE"
                      aria-required={'mandatory' === answer.oe}
                      required={'mandatory' === answer.oe}
                      onChange={event => onCommentChange(answerIndex, event)}
                      onFocus={onCommentFocus}
                    />
                  </label>
                )}
              </div>

              {answer.image && answer.image?.expand && (
                <div
                  onClick={event => onImageClicked(event, answerIndex, answer)}
                  className={style.expandWrapper}
                  data-testid="SW-Question-ExpandedImage"
                >
                  <ImageAsset
                    asset={answer.image}
                    className={style.AnswerBigImage}
                    width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                    size="medium"
                    onFullImageLoaded={frameInteractionStart}
                    previewLabelTemplate={() => (
                      <TextWithOptions text={answer.text} options={previewOptions} rules={sub?.[answer.id]} />
                    )}
                  />
                </div>
              )}
            </div>
          )
        })
      )}
    </fieldset>
  )
}

export default CheckboxesView
